import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  CBadge, CButton, CCard, CCardBody, CCardHeader, CCol, CFormInput, CInputGroup, CInputGroupText, CFormLabel, CFormSwitch, CFormSelect, CRow, CSpinner,
  CTab, CTabContent, CTabList, CTabPanel, CTabs,
  CModal, CModalBody, CModalFooter, CModalHeader, CModalTitle, CTooltip,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilBan, cilCheckCircle, cilPencil, cilTrash, cilZoom } from '@coreui/icons'
import { api } from '../api/client'
import { capitalizarPalabras } from '../utils/texto'
import { simboloMoneda } from '../utils/moneda'
import { NumericFormat } from 'react-number-format'
import WwGrid, { WwColumn } from '../components/ww/WwGrid'
import WwFilterBar from '../components/ww/WwFilterBar'
import { estadoEmpresa, ESTADOS_EMPRESA_OPCIONES } from '../utils/estadoEmpresa'
import { planEmpresaLabel, PLANES_EMPRESA } from '../utils/planEmpresa'
import { ENTIDAD_PAGO, TIPO_PAGO } from '../utils/pagoLabels'

// Módulos de la empresa. Campos REALES del esquema GX (tabla Cuenta):
// - Facturación → EmpresaModuloFacturacion (empresageneral.cs:991-998)
// - Fotos (Adicional Mes) → EmpresaFactura bool, caption GX "Fotos Adicional
//   Mes" (cuenta.cs:694-701)
// - Gráficas Gerenciales → EmpresaLibreNumerico3 = PlanTronia 0-3 (Constantes.cs)
// - TronIA → EmpresaLibreCaracter1 = EstadoPlanTronia (ACTIVO/INACTIVO)
// Impresión y WhatsApp/SMS NO son módulos de empresa: se activan por ZONA
// (ZonaImpresora, ZonaSMS — activarimpresora.cs).
const columnasEmpresas: WwColumn[] = [
  { key: 'empresaCodigo', title: 'Código' },
  { key: 'empresaNombre', title: 'Empresa' },
  {
    key: 'empresaEstado', title: 'Estado',
    render: (row) => {
      const e = estadoEmpresa(Number(row.empresaEstado ?? 0))
      return <CBadge color={e.color}>{e.label}</CBadge>
    },
    // [RGM] 2026-10-03 (pedido Rafael: el .xlsx debe mostrar el TEXTO visible):
    // exportValue espeja el render (mismo estadoEmpresa().label).
    exportValue: (row) => estadoEmpresa(Number(row.empresaEstado ?? 0)).label,
  },
  { key: 'empresaFechaCreacion', title: 'Fecha registro', type: 'date' },
    {
      key: 'empresaLibreNumerico2', title: 'Plan',
      // [RGM] 2026-09-17 (pedido Rafael: "me muestra plan en números"): etiquetas
      // del plan (PlanSiprinia 1/2/3 — planes del registro).
      render: (row) => planEmpresaLabel(row.empresaLibreNumerico2),
      // [RGM] 2026-10-03 (pedido Rafael: el .xlsx debe mostrar el TEXTO visible):
      // exportValue espeja el render (mismo planEmpresaLabel).
      exportValue: (row) => planEmpresaLabel(row.empresaLibreNumerico2),
    },
    {
      // [RGM] 2026-09-17 (pedido Rafael: "no me salen los planes de TronIA…
      // en grilla de empresa"): plan TronIA + estado (PRUEBA/ACTIVO) en la grilla.
      key: 'empresaLibreNumerico3', title: 'TronIA', minWidth: 130,
      render: (row) => {
        const plan = Number(row.empresaLibreNumerico3 ?? 0)
        const estado = String(row.empresaLibreCaracter1 ?? '').trim()
        const nombre = ({ 0: 'Ninguno', 1: 'Inicial', 2: 'Profesional', 3: 'Empresarial' } as Record<number, string>)[plan] ?? '—'
        const color = estado === 'ACTIVO' ? 'success' : estado === 'PRUEBA' ? 'warning' : 'secondary'
        const etiqueta = estado === 'ACTIVO' ? 'Activo' : estado === 'PRUEBA' ? 'Prueba' : 'Inactivo'
        return (
          <span className="d-inline-flex align-items-center gap-1">
            {plan > 0 ? nombre : <span className="text-body-secondary">Sin plan</span>}
            {plan > 0 && <CBadge color={color}>{etiqueta}</CBadge>}
          </span>
        )
      },
      // [RGM] 2026-10-03 (pedido Rafael: el .xlsx debe mostrar el TEXTO visible):
      // texto plano del render — "Inicial Activo" (plan + estado) o "Sin plan".
      exportValue: (row) => {
        const plan = Number(row.empresaLibreNumerico3 ?? 0)
        const estado = String(row.empresaLibreCaracter1 ?? '').trim()
        const nombre = ({ 0: 'Ninguno', 1: 'Inicial', 2: 'Profesional', 3: 'Empresarial' } as Record<number, string>)[plan] ?? '—'
        const etiqueta = estado === 'ACTIVO' ? 'Activo' : estado === 'PRUEBA' ? 'Prueba' : 'Inactivo'
        return plan > 0 ? `${nombre} ${etiqueta}` : 'Sin plan'
      },
    },
    {
      // [RGM] 2026-09-17 (pedido Rafael: "…y módulos activados en grilla de
      // empresa"): módulos vigentes de la empresa (Fotos = EmpresaFactura;
      // Facturación = EmpresaModuloFacturacion, smallint GX). Cambio Sencilla
      // y Domicilio se retiraron (módulos que no van — decisión Rafael).
      key: 'modulos', title: 'Módulos activados', minWidth: 180,
      render: (row) => {
        const modulos = [
          row.empresaFactura ? 'Fotos' : null,
          Number(row.empresaModuloFacturacion ?? 0) > 0 ? 'Facturación' : null,
        ].filter(Boolean)
        return modulos.length
          ? <span>{modulos.join(' · ')}</span>
          : <span className="text-body-secondary">—</span>
      },
      // [RGM] 2026-10-03 (pedido Rafael: el .xlsx debe mostrar el TEXTO visible):
      // mismo texto plano del render (módulos unidos por ' · ' / fallback '—').
      exportValue: (row) => {
        const modulos = [
          row.empresaFactura ? 'Fotos' : null,
          Number(row.empresaModuloFacturacion ?? 0) > 0 ? 'Facturación' : null,
        ].filter(Boolean)
        return modulos.length ? modulos.join(' · ') : '—'
      },
    },
  // [RGM] 2026-09-17 (pedido Rafael): cantidad de rutas y clientes (info GX).
  { key: 'rutas', title: 'Rutas', type: 'number' },
  { key: 'clientes', title: 'Clientes', type: 'number' },
  // [RGM] 2026-09-17 (pedido Rafael: "no me sale la tarifa en la grilla"):
  // tarifa mensual REAL (base por rutas/fotos + plan TronIA activo + consumo;
  // mismo cálculo de la liquidación, backend).
  { key: 'tarifa', title: 'Tarifa mensual', type: 'currency' },
  { key: 'empresaSaldoActual', title: 'Saldo actual', type: 'currency' },
  { key: 'empresaTotalPagos', title: 'Total pagos', type: 'currency' },
  { key: 'empresaUltimoPago', title: 'Último pago', type: 'date' },
  { key: 'empresaProximoCobro', title: 'Próximo cobro', type: 'date' },
]

const columnasPagos: WwColumn[] = [
  { key: 'pagoNro', title: 'Nro', type: 'number' },
  { key: 'pagoFecha', title: 'Fecha', type: 'date' },
  { key: 'pagoHora', title: 'Hora' },
  { key: 'pagoValor', title: 'Valor', type: 'currency' },
  { key: 'pagoProximoValor', title: 'Próximo valor', type: 'currency' },
  { key: 'pagoProximoCobro', title: 'Próximo cobro', type: 'date' },
  { key: 'pagoObservacion', title: 'Observación' },
  // [RGM] 2026-09-17 (reporte Rafael: "en pagos empresa… entidad y tipo aún
  // sale en números en grilla"): entidad/tipo/confirmado en TEXTO (combos GX).
  // [RGM] 2026-10-03 (pedido Rafael: el .xlsx debe mostrar el TEXTO visible):
  // exportValue espeja el render en las TRES columnas (mismos criterios de Pagos).
  {
    key: 'pagoEntidad', title: 'Entidad',
    render: (row) => ENTIDAD_PAGO[Number(row.pagoEntidad)] ?? '—',
    exportValue: (row) => ENTIDAD_PAGO[Number(row.pagoEntidad)] ?? '—',
  },
  { key: 'pagoNombreRemitente', title: 'Remitente' },
  {
    key: 'pagoConfirmado', title: 'Confirmado',
    render: (row) => (Number(row.pagoConfirmado) === 1 ? 'Si' : Number(row.pagoConfirmado) === 2 ? 'No' : ''),
    exportValue: (row) => (Number(row.pagoConfirmado) === 1 ? 'Si' : Number(row.pagoConfirmado) === 2 ? 'No' : ''),
  },
  { key: 'pagoCiudad', title: 'Ciudad' },
  { key: 'pagoPais', title: 'País' },
  {
    key: 'pagoTipo', title: 'Tipo',
    render: (row) => TIPO_PAGO[Number(row.pagoTipo)] ?? '—',
    exportValue: (row) => TIPO_PAGO[Number(row.pagoTipo)] ?? '—',
  },
]



// Empresas (global) — ruta /superadmin/empresas.
// Al seleccionar una empresa la grilla se COLAPSA mostrando solo la empresa
// elegida y se abren los tabs: Pagos (historial), Usuarios (de la empresa)
// y Módulos (tarjetas con activar/desactivar).
const SuperAdminEmpresasPage: React.FC = () => {
  const [empresas, setEmpresas] = useState<any[]>([])
  const formRef = useRef<HTMLFormElement>(null)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  // Empresa seleccionada (null = listado completo visible)
  const [seleccionada, setSeleccionada] = useState<any | null>(null)
  // ── Filtros rápidos comunes: estado y plan ──
  const [filtroEstado, setFiltroEstado] = useState('')
  const [filtroPlan, setFiltroPlan] = useState('')
  const [pagos, setPagos] = useState<any[]>([])
  const [cargandoPagos, setCargandoPagos] = useState(false)
  const [usuarios, setUsuarios] = useState<any[]>([])
  const [rutas, setRutas] = useState<any[]>([])
  const [cargandoRutas, setCargandoRutas] = useState(false)
  const [guardandoModulo, setGuardandoModulo] = useState(false)
  // Modal cambiar clave + acciones de usuario
  const [claveUsuario, setClaveUsuario] = useState<any | null>(null)
  const [nuevaClave, setNuevaClave] = useState('')
  const [confirmacion, setConfirmacion] = useState('')
  const [modalError, setModalError] = useState('')
  const [guardandoClave, setGuardandoClave] = useState(false)
  // Modal editar empresa (empresaww Mode="UPD" → empresa_bc.cs:3182 BC000F8)
  const [editandoEmpresa, setEditandoEmpresa] = useState<any | null>(null)
  const [formEmpresa, setFormEmpresa] = useState<any>({})
  const [guardandoEmpresa, setGuardandoEmpresa] = useState(false)
  // [RGM] 2026-09-17 (pedido Rafael): bloqueo manual de mora — réplica del
  // botón "Blouear Empresas enMora" del GX (actualizacionescybertronica.cs
  // evento E'BLOUEAR EMPRESAS ENMORA' → bloquearempresasenmora.cs:128).
  // Default: el 5 del mes actual (la fecha que Rafael llena siempre).
  const [fechaMora, setFechaMora] = useState(() => {
    const h = new Date()
    const mm = String(h.getMonth() + 1).padStart(2, '0')
    return `${h.getFullYear()}-${mm}-05`
  })
  const [bloqueandoMora, setBloqueandoMora] = useState(false)

  useEffect(() => {
    api.get<any[]>('/superadmin/empresas')
      .then(setEmpresas)
      .catch((e: any) => setError(e.message))
      .finally(() => setCargando(false))
    // Usuarios globales: el tab Usuarios filtra por la empresa seleccionada.
    api.get<any[]>('/superadmin/usuarios').then(setUsuarios).catch(() => undefined)
  }, [])

  // Pagos de la empresa seleccionada (GET /api/pagos?empresa= — pagoww GX)
  useEffect(() => {
    if (!seleccionada) {
      setPagos([])
      return
    }
    setCargandoPagos(true)
    api.get<any[]>(`/pagos?empresa=${seleccionada.empresaCodigo}`)
      .then(setPagos)
      .catch((e: any) => setError(e.message))
      .finally(() => setCargandoPagos(false))
  }, [seleccionada])

  // Filas filtradas por estado/plan (client-side, patrón WwFilterBar)
  const empresasFiltradas = useMemo(() => empresas.filter((e) => {
    if (filtroEstado) {
      const label = estadoEmpresa(Number(e.empresaEstado ?? 0)).label
      if (label !== filtroEstado) return false
    }
    // [RGM] 2026-09-17 (reporte Rafael): el filtro de Plan compara ETIQUETA
    // ("Unipersonal"), no el número crudo — opciones del select en texto.
    if (filtroPlan && planEmpresaLabel(e.empresaLibreNumerico2) !== filtroPlan) return false
    return true
  }), [empresas, filtroEstado, filtroPlan])
  // [RGM] 2026-09-17 (pregunta Rafael: "¿plan Duo aparece cuando haya en la
  // búsqueda?"): el filtro lista SIEMPRE los 4 planes del dominio (igual que
  // el filtro de Estados, que muestra todos) — no depende de que exista una
  // empresa con ese plan en la lista.
  const planes = Object.values(PLANES_EMPRESA)
  const chips = [
    filtroEstado ? { clave: 'estado', label: `Estado: ${filtroEstado}` } : null,
    filtroPlan ? { clave: 'plan', label: `Plan: ${filtroPlan}` } : null,
  ].filter(Boolean) as { clave: string; label: string }[]
  const limpiarFiltros = () => { setFiltroEstado(''); setFiltroPlan('') }
  const quitarFiltro = (clave: string) => { if (clave === 'estado') setFiltroEstado(''); if (clave === 'plan') setFiltroPlan('') }

  const usuariosEmpresa = seleccionada
    ? usuarios.filter((u) => u.empresaCodigo === seleccionada.empresaCodigo)
    : []

  // Rutas de la empresa: ZonaImpresora/ZonaSMS (activarimpresora.cs)
  useEffect(() => {
    if (!seleccionada) {
      setRutas([])
      return
    }
    setCargandoRutas(true)
    api.get<any[]>(`/rutas?empresa=${seleccionada.empresaCodigo}`)
      .then(setRutas)
      .catch(() => undefined)
      .finally(() => setCargandoRutas(false))
  }, [seleccionada])

  // Activar/desactivar módulo — PUT /superadmin/empresas/{codigo}/modulos
  // (empresa_bc.cs DO UPDATE; Facturación = EmpresaModuloFacturacion,
  // TronIA = EstadoPlanTronia EmpresaLibreCaracter1).
  const cambiarModulo = useCallback(async (
    campo: 'moduloFacturacion' | 'empresaFactura' | 'planTronia' | 'troniaEstado',
    valor: boolean,
  ) => {
    if (!seleccionada) return
    setGuardandoModulo(true)
    setError('')
    try {
      const body = campo === 'moduloFacturacion'
        ? { moduloFacturacion: valor ? 1 : 0 }
        : campo === 'empresaFactura'
          ? { empresaFactura: valor }
          : campo === 'planTronia'
            ? { planTronia: valor ? 1 : 0 }
            : { troniaEstado: valor ? 'ACTIVO' : 'INACTIVO' }
      await api.put(`/superadmin/empresas/${seleccionada.empresaCodigo}/modulos`, body)
      setSeleccionada({
        ...seleccionada,
        empresaModuloFacturacion: campo === 'moduloFacturacion' ? (valor ? 1 : 0) : seleccionada.empresaModuloFacturacion,
        empresaFactura: campo === 'empresaFactura' ? valor : seleccionada.empresaFactura,
        empresaLibreNumerico3: campo === 'planTronia' ? (valor ? 1 : 0) : seleccionada.empresaLibreNumerico3,
        empresaLibreCaracter1: campo === 'troniaEstado' ? (valor ? 'ACTIVO' : 'INACTIVO') : seleccionada.empresaLibreCaracter1,
      })
    } catch (e: any) {
      setError(e.message)
    } finally {
      setGuardandoModulo(false)
    }
  }, [seleccionada])

  // [RGM] 2026-10-08 (pedido Rafael: "decide algo bien comercial" — combo
  // Actividad EN FORMATO comercial: sin números ni descripciones técnicas).
  // Opciones comerciales del combo que reemplaza los dos switches
  // (Facturación + Solo Facturación): mismos campos reales de siempre.
  const ACTIVIDAD_OPCIONES = [
    { value: 1, label: 'Prestamos Personales' },
    { value: 2, label: 'Prestamos y Ventas a Credito' },
    { value: 3, label: 'Venta de Mercancias de Contado y Credito' },
  ]

  // Mismo mecanismo de guardado de los switches anteriores (PUT
  // /superadmin/empresas/{cod}/modulos): 1 → sin módulo y sin
  // solo-facturación; 2 → módulo facturación activo (híbrido); 3 →
  // solo-facturación (caracter3 "SI" + módulo fijo).
  const cambiarActividad = useCallback(async (valor: number) => {
    if (!seleccionada) return
    const etiqueta = ACTIVIDAD_OPCIONES.find((o) => o.value === valor)?.label ?? ''
    const ok = window.confirm(`¿Cambiar la actividad de esta empresa a "${etiqueta}"?`)
    if (!ok) return
    setGuardandoModulo(true)
    setError('')
    try {
      const body = valor === 3
        ? { soloFacturacion: true, moduloFacturacion: 1 }
        : valor === 2
          ? { soloFacturacion: false, moduloFacturacion: 1 }
          : { soloFacturacion: false, moduloFacturacion: 0 }
      await api.put(`/superadmin/empresas/${seleccionada.empresaCodigo}/modulos`, body)
      setSeleccionada({
        ...seleccionada,
        empresaLibreCaracter3: valor === 3 ? 'SI' : '',
        empresaModuloFacturacion: valor === 1 ? 0 : 1,
      })
    } catch (e: any) {
      setError(e.message)
    } finally {
      setGuardandoModulo(false)
    }
  }, [seleccionada])

  // Estado de TronIA: activo si el plan está en ACTIVO o PRUEBA (operando)
  // [RGM] 2026-09-17 (pedido Rafael "para cambiar el plan de TronIA"): el plan
  // TronIA se elige con SELECT (0-3) — EmpresaLibreNumerico3. Su costo entra a
  // la liquidación mensual cuando no está en PRUEBA (PagoService:64-72).
  const cambiarPlanTronia = useCallback(async (valor: number) => {
    if (!seleccionada) return
    setGuardandoModulo(true)
    setError('')
    try {
      await api.put(`/superadmin/empresas/${seleccionada.empresaCodigo}/modulos`, { planTronia: valor })
      setSeleccionada({ ...seleccionada, empresaLibreNumerico3: valor })
    } catch (e: any) {
      setError(e.message)
    } finally {
      setGuardandoModulo(false)
    }
  }, [seleccionada])

  // Activar/desactivar Impresión/WS de una RUTA — PUT /superadmin/empresas/
  // {emp}/rutas/{zona}/modulos (activarimpresora.cs / activarsms.cs GX; el
  // desactivar sigue zona_bc.cs:967-968).
  const cambiarRutaModulo = useCallback(async (ruta: any, campo: 'impresora' | 'sms', valor: boolean) => {
    if (!seleccionada) return
    setGuardandoModulo(true)
    setError('')
    try {
      const body = campo === 'impresora' ? { impresora: valor } : { sms: valor }
      await api.put(
        `/superadmin/empresas/${seleccionada.empresaCodigo}/rutas/${ruta.zonaCodigo}/modulos`,
        body,
      )
      setRutas((prev) => prev.map((r) =>
        r.zonaCodigo === ruta.zonaCodigo
          ? { ...r, [campo === 'impresora' ? 'zonaImpresora' : 'zonaSMS']: valor ? 1 : 0 }
          : r,
      ))
    } catch (e: any) {
      setError(e.message)
    } finally {
      setGuardandoModulo(false)
    }
  }, [seleccionada])

  // Recargar usuarios globales (el tab Usuarios filtra por la empresa)
  const recargarUsuarios = useCallback(() => {
    api.get<any[]>('/superadmin/usuarios').then(setUsuarios).catch(() => undefined)
  }, [])

  // Bloquear/desbloquear — réplica del DO UPDATE de usuarioww.cs:2588-2600
  const toggleEstadoUsuario = async (row: any) => {
    setError('')
    const nuevo = row.usuarioEstado === 'Activo' ? 'Bloqueado' : 'Activo'
    try {
      await api.put(`/superadmin/usuarios/${row.usuarioCodigo}/estado`, { estado: nuevo })
      recargarUsuarios()
    } catch (e: any) {
      setError(e.message)
    }
  }

  // Matar sesión (forzar logout): UsuarioOnline = false (flag de sesión activa)
  const matarSesion = async (row: any) => {
    setError('')
    try {
      await api.put(`/superadmin/usuarios/${row.usuarioCodigo}/sesion`, { online: false })
      recargarUsuarios()
    } catch (e: any) {
      setError(e.message)
    }
  }

  // Eliminar usuario — réplica del DLT de usuarioww.cs (UsuariosController.cs:109
  // valida Supervisor/Cobrador asociado)
  const eliminarUsuario = async (row: any) => {
    setError('')
    if (!window.confirm(`¿Eliminar el usuario ${row.usuarioCodigo}?`)) return
    try {
      await api.delete(`/usuarios/${row.usuarioCodigo}?empresa=${row.empresaCodigo}`)
      recargarUsuarios()
    } catch (e: any) {
      setError(e.message)
    }
  }

  // Cambiar clave — réplica de gamchangepassword.cs (GAM setpassword)
  const guardarClave = async (e: React.FormEvent) => {
    e.preventDefault()
    setModalError('')
    if (nuevaClave.length < 6) { setModalError('La nueva clave debe tener al menos 6 caracteres'); return }
    if (nuevaClave !== confirmacion) { setModalError('La confirmación no coincide con la nueva clave'); return }
    setGuardandoClave(true)
    try {
      await api.put(`/superadmin/usuarios/${claveUsuario.usuarioCodigo}/clave`, { nuevaClave, confirmacion })
      setClaveUsuario(null)
      recargarUsuarios()
    } catch (err: any) {
      setModalError(err.message)
    } finally {
      setGuardandoClave(false)
    }
  }

  // Editar empresa — réplica del WW empresa Mode="UPD" (empresa_bc.cs:3182,
  // cursor BC000F8: campos de negocio de Cuenta editables).
  const abrirEditarEmpresa = (row: any) => {
    setFormEmpresa({
      empresaNombre: row.empresaNombre ?? '',
      empresaRepreLegal: row.empresaRepreLegal ?? '',
      empresaDireccion: row.empresaDireccion ?? '',
      empresaTelefono: row.empresaTelefono ?? '',
      empresaCelular: row.empresaCelular ?? '',
      empresaEmail: row.empresaEmail ?? '',
      empresaPais: row.empresaPais ?? '',
      empresaCiudad: row.empresaCiudad ?? '',
      empresaTipoMoneda: row.empresaTipoMoneda ?? '',
      empresaMotivoSaldo: row.empresaMotivoSaldo ?? '',
      empresaEstado: row.empresaEstado ?? 1,
      empresaSaldoActual: row.empresaSaldoActual ?? 0,
      // Campos restantes del form GX (empresageneral.cs — BC UPD
      // empresa_bc.cs:3182)
      empresaDescripcion: row.empresaDescripcion ?? '',
      empresaHoraActual: row.empresaHoraActual ?? '',
      empresaTipoServicio: row.empresaTipoServicio ?? '',
      empresaHorarioApertura: row.empresaHorarioApertura ?? '',
      empresaHorarioCierre: row.empresaHorarioCierre ?? '',
      empresaValorEmpaque: row.empresaValorEmpaque ?? 0,
      empresaBalance: row.empresaBalance ?? 0,
      empresaActividadComercial: row.empresaActividadComercial ?? 0,
      empresaFactura: row.empresaFactura ?? false,
      empresaTrabajaSabado: row.empresaTrabajaSabado ?? false,
      empresaTrabajaDomingo: row.empresaTrabajaDomingo ?? false,
      empresaTrabajaFestivo: row.empresaTrabajaFestivo ?? false,
      empresaFechaCreacion: row.empresaFechaCreacion ?? '',
      empresaHoraCreacion: row.empresaHoraCreacion ?? '',
      empresaUltimoPago: row.empresaUltimoPago ?? '',
      empresaProximoCobro: row.empresaProximoCobro ?? '',
      empresaTotalPagos: row.empresaTotalPagos ?? 0,
      empresaLibreNumerico1: row.empresaLibreNumerico1 ?? 0,
      empresaLibreNumerico2: row.empresaLibreNumerico2 ?? 0,
      empresaLibreNumerico3: row.empresaLibreNumerico3 ?? 0,
      empresaLibreValor1: row.empresaLibreValor1 ?? 0,
      empresaLibreValor2: row.empresaLibreValor2 ?? 0,
      empresaLibreValor3: row.empresaLibreValor3 ?? 0,
      empresaLibreCaracter1: row.empresaLibreCaracter1 ?? '',
      empresaLibreCaracter2: row.empresaLibreCaracter2 ?? '',
      empresaLibreCaracter3: row.empresaLibreCaracter3 ?? '',
      empresaModuloFacturacion: row.empresaModuloFacturacion ?? 0,
    })
    setEditandoEmpresa(row)
  }

  const guardarEmpresa = async (e: React.FormEvent) => {
    e.preventDefault()
    setModalError('')
    setGuardandoEmpresa(true)
    try {
      await api.put(`/superadmin/empresas/${editandoEmpresa.empresaCodigo}`, {
        empresaNombre: formEmpresa.empresaNombre,
        empresaRepreLegal: formEmpresa.empresaRepreLegal,
        empresaDireccion: formEmpresa.empresaDireccion,
        empresaTelefono: formEmpresa.empresaTelefono,
        empresaCelular: formEmpresa.empresaCelular,
        empresaEmail: formEmpresa.empresaEmail,
        empresaPais: formEmpresa.empresaPais,
        empresaCiudad: formEmpresa.empresaCiudad,
        empresaTipoMoneda: formEmpresa.empresaTipoMoneda,
        empresaMotivoSaldo: formEmpresa.empresaMotivoSaldo,
        empresaEstado: Number(formEmpresa.empresaEstado),
        empresaSaldoActual: Number(formEmpresa.empresaSaldoActual),
        empresaDescripcion: formEmpresa.empresaDescripcion,
        empresaHoraActual: formEmpresa.empresaHoraActual,
        empresaTipoServicio: formEmpresa.empresaTipoServicio,
        empresaHorarioApertura: formEmpresa.empresaHorarioApertura,
        empresaHorarioCierre: formEmpresa.empresaHorarioCierre,
        empresaValorEmpaque: Number(formEmpresa.empresaValorEmpaque) || 0,
        empresaBalance: Number(formEmpresa.empresaBalance) || 0,
        empresaActividadComercial: Number(formEmpresa.empresaActividadComercial) || 0,
        empresaFactura: !!formEmpresa.empresaFactura,
        empresaTrabajaSabado: !!formEmpresa.empresaTrabajaSabado,
        empresaTrabajaDomingo: !!formEmpresa.empresaTrabajaDomingo,
        empresaTrabajaFestivo: !!formEmpresa.empresaTrabajaFestivo,
        empresaFechaCreacion: formEmpresa.empresaFechaCreacion || null,
        empresaHoraCreacion: formEmpresa.empresaHoraCreacion || null,
        empresaUltimoPago: formEmpresa.empresaUltimoPago || null,
        empresaProximoCobro: formEmpresa.empresaProximoCobro || null,
        empresaTotalPagos: Number(formEmpresa.empresaTotalPagos) || 0,
        empresaLibreNumerico1: Number(formEmpresa.empresaLibreNumerico1) || 0,
        empresaLibreNumerico2: Number(formEmpresa.empresaLibreNumerico2) || 0,
        empresaLibreNumerico3: Number(formEmpresa.empresaLibreNumerico3) || 0,
        empresaLibreValor1: Number(formEmpresa.empresaLibreValor1) || 0,
        empresaLibreValor2: Number(formEmpresa.empresaLibreValor2) || 0,
        empresaLibreValor3: Number(formEmpresa.empresaLibreValor3) || 0,
        empresaLibreCaracter1: formEmpresa.empresaLibreCaracter1 || null,
        empresaLibreCaracter2: formEmpresa.empresaLibreCaracter2 || null,
        empresaLibreCaracter3: formEmpresa.empresaLibreCaracter3 || null,
        empresaModuloFacturacion: Number(formEmpresa.empresaModuloFacturacion) || 0,
      })
      setEditandoEmpresa(null)
      api.get<any[]>('/superadmin/empresas').then(setEmpresas).catch(() => undefined)
      if (seleccionada?.empresaCodigo === editandoEmpresa.empresaCodigo) {
        setSeleccionada({ ...seleccionada, ...formEmpresa })
      }
    } catch (err: any) {
      setModalError(err.message)
    } finally {
      setGuardandoEmpresa(false)
    }
  }

  // Eliminar empresa — réplica del BC Cuenta DLT (empresa_bc.cs:835-880; el
  // backend valida referencias: Pagos/Socios/Préstamos asociados).
  const eliminarEmpresa = (row: any) => {
    if (!window.confirm(`¿Eliminar la empresa ${row.empresaCodigo}?`)) return
    api.delete(`/superadmin/empresas/${row.empresaCodigo}`)
      .then(() => {
        setEmpresas((prev) => prev.filter((e) => e.empresaCodigo !== row.empresaCodigo))
        if (seleccionada?.empresaCodigo === row.empresaCodigo) setSeleccionada(null)
      })
      .catch((e: any) => setError(e.message))
  }

  // [RGM] 2026-09-17 (pedido Rafael): bloquear en mora (estado 3) las empresas
  // que no subieron el pago de la fecha indicada — réplica del botón GX
  // (bloquearempresasenmora.cs:128; excluye Prueba). Al bloquear, el menú de
  // esa empresa queda solo en "Mi Cuenta" (MenuController activa = estado≠3)
  // para que puedan subir el pago; al pagar, el sistema las reactiva a 2.
  const bloquearMora = () => {
    if (!window.confirm(`¿Bloquear en mora las empresas con cobro vencido al ${fechaMora}?`)) return
    setBloqueandoMora(true)
    setError('')
    api.post<{ bloqueadas: number }>('/superadmin/empresas/bloquear-mora', { fecha: fechaMora })
      .then(async (res) => {
        const lista = await api.get<any[]>('/superadmin/empresas')
        setEmpresas(lista)
        window.alert(`${res.bloqueadas} empresa(s) bloqueada(s) en mora`)
      })
      .catch((e: any) => setError(e.message))
      .finally(() => setBloqueandoMora(false))
  }

  // Activar / Suspender (EmpresaEstado 1 / 3 — cuenta_bc.cs:2046 BC000C7)
  const cambiarEstadoEmpresa = (row: any, estado: number) => {
    api.put(`/superadmin/empresas/${row.empresaCodigo}`, { empresaEstado: estado })
      .then(() => {
        api.get<any[]>('/superadmin/empresas').then(setEmpresas).catch(() => undefined)
      })
      .catch((e: any) => setError(e.message))
  }

  const troniaActivo = ['ACTIVO', 'PRUEBA'].includes((seleccionada?.empresaLibreCaracter1 ?? '').trim())
  // [RGM] 2026-10-08 (combo Actividad — pedido Rafael): estado actual derivado
  // de los dos flags reales: caracter3 "SI"/"FACTURACION" = 3 (Venta de
  // Mercancias); si no, módulo facturación activo = 2 (Prestamos y Ventas);
  // si no, 1 (Prestamos Personales).
  const actividadActual = ['SI', 'FACTURACION'].includes(
    String(seleccionada?.empresaLibreCaracter3 ?? '').trim().toUpperCase(),
  )
    ? 3
    : (seleccionada?.empresaModuloFacturacion ?? 0) === 1
      ? 2
      : 1
  const fotosActivas = seleccionada?.empresaFactura === true

  // Rutas de la empresa: Impresión (ZonaImpresora) y WS/SMS (ZonaSMS) se
  // activan POR RUTA (activarimpresora.cs / activarsms.cs — Zona.cs:19-20);
  // los switches editan directo el flag de la ruta.
  // Usuarios de la empresa con acciones: bloquear, clave, eliminar, matar
  // sesión (usuarioww.cs UPD/DLT; AuthService.cs:37 valida el estado).
  const columnasUsuarios: WwColumn[] = [
    { key: 'usuarioCodigo', title: 'Usuario' },
    { key: 'usuarioNombre', title: 'Nombre' },
    { key: 'usuarioEmail', title: 'Email' },
    { key: 'rolNombre', title: 'Rol' },
    {
      key: 'usuarioEstado', title: 'Estado',
      render: (row) => (
        <CBadge color={row.usuarioEstado === 'Activo' ? 'success' : 'danger'}>
          {row.usuarioEstado || '—'}
        </CBadge>
      ),
      exportValue: (row) => row.usuarioEstado || '—',
    },
    { key: 'usuarioOnline', title: 'Online', type: 'bool' },
    {
      key: 'acciones', title: 'Acciones', minWidth: 320,
      render: (row) => (
        <div className="d-flex gap-1 flex-wrap">
          <CButton size="sm" color={row.usuarioEstado === 'Activo' ? 'danger' : 'success'}
            disabled={row.usuarioCodigo === 'admin'}
            onClick={() => toggleEstadoUsuario(row)}>
            {row.usuarioEstado === 'Activo' ? 'Bloquear' : 'Desbloquear'}
          </CButton>
          <CButton size="sm" color="outline-secondary" onClick={() => { setModalError(''); setNuevaClave(''); setConfirmacion(''); setClaveUsuario(row) }}>
            Cambiar Clave
          </CButton>
          {row.usuarioOnline && (
            <CButton size="sm" color="warning" onClick={() => matarSesion(row)}>
              Matar Sesión
            </CButton>
          )}
          <CButton size="sm" color="danger" disabled={row.usuarioCodigo === 'admin'}
            onClick={() => eliminarUsuario(row)}>
            Eliminar
          </CButton>
        </div>
      ),
    },
  ]

  // Acciones del WW empresa (empresageneral.cs): Ver (colapso+tabs),
  // Editar (UPD), Eliminar (DLT), Activar/Suspender (estado).
  const accionesEmpresas: WwColumn[] = [
    ...columnasEmpresas,
    {
      key: 'acciones', title: 'Acciones', minWidth: 170,
      // [RGM] 2026-09-29 (pedido Rafael): acciones de fila STICKY (fijas a la
      // derecha al hacer scroll horizontal).
      sticky: 'right',
      render: (row) => (
        // [RGM] 2026-09-17 (reporte Rafael): la columna Acciones NO debe
        // propagar el clic al onRowClick de la fila — antes, tocar
        // Activar/Suspender/Editar abría también la vista del detalle.
        // (Patrón stopPropagation ya usado en RutasPage, zonaBase.)
        // [RGM] 2026-09-17 (pedido Rafael): acciones con ICONOS + tooltip
        // CoreUI (antes botones de texto).
        <div className="d-flex gap-1 flex-wrap" onClick={(e) => e.stopPropagation()}>
          <CTooltip content="Ver">
            <CButton size="sm" color="primary" aria-label="Ver" onClick={() => setSeleccionada(row)}>
              <CIcon icon={cilZoom} />
            </CButton>
          </CTooltip>
          <CTooltip content="Editar">
            <CButton size="sm" color="outline-secondary" aria-label="Editar" onClick={() => abrirEditarEmpresa(row)}>
              <CIcon icon={cilPencil} />
            </CButton>
          </CTooltip>
          {/* [RGM] 2026-09-16: semántica GX — Activar = 2 (activa),
              Suspender = 3 (mora). Antes usaba 1 como "activa" (invertido). */}
          {Number(row.empresaEstado ?? 0) === 2 ? (
            <CTooltip content="Suspender">
              <CButton size="sm" color="warning" aria-label="Suspender" onClick={() => cambiarEstadoEmpresa(row, 3)}>
                <CIcon icon={cilBan} />
              </CButton>
            </CTooltip>
          ) : (
            <CTooltip content="Activar">
              <CButton size="sm" color="success" aria-label="Activar" onClick={() => cambiarEstadoEmpresa(row, 2)}>
                <CIcon icon={cilCheckCircle} />
              </CButton>
            </CTooltip>
          )}
          <CTooltip content="Eliminar">
            <CButton size="sm" color="danger" aria-label="Eliminar" onClick={() => eliminarEmpresa(row)}>
              <CIcon icon={cilTrash} />
            </CButton>
          </CTooltip>
        </div>
      ),
    },
  ]

  const columnasRutas: WwColumn[] = [
    { key: 'zonaCodigo', title: 'Zona', type: 'number' },
    { key: 'zonaNombre', title: 'Ruta' },
    {
      key: 'zonaImpresora', title: 'Impresión', minWidth: 110,
      render: (row) => (
        <CFormSwitch
          id={`ruta-imp-${row.zonaCodigo}`}
          checked={Number(row.zonaImpresora ?? 0) === 1}
          disabled={guardandoModulo}
          onChange={(e) => cambiarRutaModulo(row, 'impresora', e.target.checked)}
        />
      ),
    },
    {
      key: 'zonaSMS', title: 'WS / SMS', minWidth: 110,
      render: (row) => (
        <CFormSwitch
          id={`ruta-sms-${row.zonaCodigo}`}
          checked={Number(row.zonaSMS ?? 0) === 1}
          disabled={guardandoModulo}
          onChange={(e) => cambiarRutaModulo(row, 'sms', e.target.checked)}
        />
      ),
    },
  ]

  return (
    <>
      <CRow className="mb-4">
        <CCol>
          <h4 className="mb-0">Empresas</h4>
          <small className="text-body-secondary">
            Seleccione una empresa para ver pagos, usuarios y módulos.
          </small>
        </CCol>
      </CRow>

      {error && <div className="alert alert-danger">{error}</div>}

      {!seleccionada ? (
        cargando ? (
          <div className="text-center py-4"><CSpinner color="primary" /></div>
        ) : (
          <>
            {/* [RGM] 2026-09-17 (pedido Rafael): bloqueo manual de mora —
                réplica del botón "Blouear Empresas enMora" del GX. */}
            <div className="d-flex align-items-end gap-2 flex-wrap mb-3">
              <div>
                <CFormLabel className="mb-1">Fecha de cobro (vencida)</CFormLabel>
                <CFormInput
                  type="date"
                  size="sm"
                  value={fechaMora}
                  onChange={(e) => setFechaMora(e.target.value)}
                  style={{ maxWidth: 170 }}
                />
              </div>
              <CButton color="danger" size="sm" disabled={bloqueandoMora} onClick={bloquearMora}>
                {bloqueandoMora ? 'Bloqueando…' : 'Bloquear en Mora'}
              </CButton>
              <small className="text-body-secondary mb-1">
                Marca en mora a las empresas que no subieron el pago de esa fecha (excluye las que están en Prueba).
              </small>
            </div>
            <WwGrid
              filters={
                <WwFilterBar
                  selects={[
                    { key: 'estado', label: 'Todos los Estados', opciones: ESTADOS_EMPRESA_OPCIONES, valor: filtroEstado, onChange: setFiltroEstado },
                    { key: 'plan', label: 'Todos los Planes', opciones: planes, valor: filtroPlan, onChange: setFiltroPlan },
                  ]}
                  chips={chips}
                  onQuitar={quitarFiltro}
                  onLimpiar={limpiarFiltros}
                />
              }
              title={editandoEmpresa
                ? `Empresas — editando ${String(editandoEmpresa.empresaCodigo).trim()}`
                : 'Empresas'}
              columns={accionesEmpresas}
              // [RGM] 2026-09-29 (pedido Rafael: "aún no se ocultan los demás
              // — no sé cuál edito"): mientras se EDITA una empresa, la grilla
              // queda solo con ella.
              rows={editandoEmpresa
                ? empresasFiltradas.filter((e) =>
                  String(e.empresaCodigo ?? '').trim() === String(editandoEmpresa.empresaCodigo).trim())
                : empresasFiltradas}
              onTotalChange={() => undefined}
              onRowClick={(row) => setSeleccionada(row)}
            />
          </>
        )
      ) : (
        <>
          {/* Empresa colapsada: solo la seleccionada + volver al listado */}
          <CCard className="shadow-sm mb-3">
            <CCardBody className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div>
                <div className="fs-5 fw-bold">
                  {seleccionada.empresaCodigo} · {seleccionada.empresaNombre}
                </div>
                <div className="text-body-secondary small mt-1">
                  {(() => { const e = estadoEmpresa(Number(seleccionada.empresaEstado ?? 0)); return <CBadge color={e.color}>{e.label}</CBadge> })()}
                  {' '}· Plan {seleccionada.empresaLibreNumerico2 ?? '—'} · Saldo {seleccionada.empresaSaldoActual ?? '—'}
                </div>
              </div>
              <CButton color="outline-secondary" size="sm" onClick={() => setSeleccionada(null)}>
                ← Volver al listado
              </CButton>
            </CCardBody>
          </CCard>

          <CTabs defaultActiveItemKey="pagos">
            <CTabList variant="tabs">
              <CTab itemKey="pagos">Pagos</CTab>
              <CTab itemKey="usuarios">Usuarios</CTab>
              <CTab itemKey="modulos">Módulos</CTab>
              <CTab itemKey="rutas">Rutas</CTab>
            </CTabList>
            <CTabContent>
              {/* Historial de pagos de la empresa (pagoww GX) */}
              <CTabPanel className="py-3" itemKey="pagos">
                {cargandoPagos ? (
                  <div className="text-center py-4"><CSpinner color="primary" /></div>
                ) : pagos.length === 0 ? (
                  <div className="text-body-secondary py-3 text-center">Sin pagos registrados</div>
                ) : (
                  <WwGrid title="Historial de pagos" columns={columnasPagos} rows={pagos} onTotalChange={() => undefined} />
                )}
              </CTabPanel>

              {/* Usuarios de la empresa seleccionada */}
              <CTabPanel className="py-3" itemKey="usuarios">
                {usuariosEmpresa.length === 0 ? (
                  <div className="text-body-secondary py-3 text-center">Sin usuarios en esta empresa</div>
                ) : (
                  <WwGrid title="Usuarios" columns={columnasUsuarios} rows={usuariosEmpresa} onTotalChange={() => undefined} />
                )}
              </CTabPanel>

              {/* Módulos: tarjetas con activar/desactivar */}
              <CTabPanel className="py-3" itemKey="modulos">
                <div className="text-body-secondary small mb-2">Pagos única vez</div>
                <CRow className="g-3 mb-4">
                  {/* [RGM] 2026-10-08 (pedido Rafael: "decide algo bien
                      comercial"): combo Actividad con textos comerciales, sin
                      números ni descripciones técnicas — reemplaza los dos
                      switches (Facturación + Solo Facturación). Mismos campos
                      reales: la elección 1/2/3 escribe módulo y caracter3. */}
                  <CCol md={4} sm={6}>
                    <CCard className="h-100 shadow-sm">
                      <CCardBody className="d-flex flex-column">
                        <div className="fw-semibold mb-2">Actividad</div>
                        <CFormSelect
                          value={actividadActual}
                          disabled={guardandoModulo}
                          onChange={(e) => cambiarActividad(Number(e.target.value))}
                        >
                          {ACTIVIDAD_OPCIONES.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </CFormSelect>
                      </CCardBody>
                    </CCard>
                  </CCol>
                  {/* Fotos (Adicional Mes) — EmpresaFactura bool, cuenta.cs:694 */}
                  <CCol md={3} sm={6}>
                    <CCard className="h-100 shadow-sm">
                      <CCardBody className="d-flex flex-column">
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="fw-semibold">Fotos</div>
                          <CFormSwitch
                            id="mod-fotos"
                            checked={fotosActivas}
                            disabled={guardandoModulo}
                            onChange={(e) => cambiarModulo('empresaFactura', e.target.checked)}
                          />
                        </div>
                        <small className="text-body-secondary">Fotos Adicional Mes (EmpresaFactura)</small>
                      </CCardBody>
                    </CCard>
                  </CCol>
                  {/* [RGM] 2026-09-17 (pedido Rafael: "para cambiar el plan
                      de TronIA desde super admin"): SELECT del plan TronIA
                      (EmpresaLibreNumerico3 — PlanTronia 0-3, precios y
                      consultas de Constantes.cs). El costo del plan entra a
                      la liquidación mensual (PagoService.CalcularTotalAPagar:
                      plan ≠ PRUEBA suma el precio). */}
                  <CCol md={3} sm={6}>
                    <CCard className="h-100 shadow-sm">
                      <CCardBody className="d-flex flex-column">
                        <div className="fw-semibold mb-2">Plan TronIA</div>
                        <CFormSelect
                          value={Number(seleccionada.empresaLibreNumerico3 ?? 0)}
                          disabled={guardandoModulo}
                          onChange={(e) => cambiarPlanTronia(Number(e.target.value))}
                        >
                          <option value={0}>(Ninguno)</option>
                          <option value={1}>Inicial — $30.000 · 60 consultas</option>
                          <option value={2}>Profesional — $125.000 · 250 consultas</option>
                          <option value={3}>Empresarial — $300.000 · 600 consultas</option>
                        </CFormSelect>
                        <small className="text-body-secondary mt-2">EmpresaLibreNumerico3</small>
                      </CCardBody>
                    </CCard>
                  </CCol>
                </CRow>
                <div className="text-body-secondary small mb-2">Planes con pago mensual</div>
                <CRow className="g-3">
                  {/* Fotos TronIA — campo real EstadoPlanTronia (EmpresaLibreCaracter1) */}
                  <CCol md={3} sm={6}>
                    <CCard className="h-100 shadow-sm">
                      <CCardBody className="d-flex flex-column">
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="fw-semibold">TronIA</div>
                          <CFormSwitch
                            id="mod-tronia"
                            checked={troniaActivo}
                            disabled={guardandoModulo}
                            onChange={(e) => cambiarModulo('troniaEstado', e.target.checked)}
                          />
                        </div>
                        <small className="text-body-secondary">Estado del plan TronIA ({seleccionada.empresaLibreCaracter1?.trim() || '—'})</small>
                      </CCardBody>
                    </CCard>
                  </CCol>
                </CRow>
              </CTabPanel>

              {/* [RGM] 2026-09-17 (pedido Rafael): pestaña propia con las RUTAS
                  de la empresa (antes vivía dentro de Módulos). */}
              <CTabPanel className="py-3" itemKey="rutas">
                <div className="text-body-secondary small mb-2">
                  Impresión y WhatsApp/SMS se activan por RUTA (ZonaImpresora, ZonaSMS — activarimpresora.cs)
                </div>
                {cargandoRutas ? (
                  <div className="text-center py-4"><CSpinner color="primary" /></div>
                ) : rutas.length === 0 ? (
                  <div className="text-body-secondary py-3 text-center">Sin rutas en esta empresa</div>
                ) : (
                  <WwGrid title="Rutas" columns={columnasRutas} rows={rutas} onTotalChange={() => undefined} />
                )}
              </CTabPanel>
            </CTabContent>
          </CTabs>
        </>
      )}

      {/* Editar empresa — TARJETA con el form completo del WW empresa
          (empresageneral.cs; BC UPD empresa_bc.cs:3182 BC000F8) */}
      {editandoEmpresa && (
        <CCard className="shadow-sm mt-4">
          <CCardHeader className="fw-semibold d-flex justify-content-between align-items-center">
            <span>
              {/* [RGM] 2026-09-29 (reporte Rafael: "solo cédula, no es claro
                  quién es"): título con nombre y representante. */}
              Editar empresa — {String(editandoEmpresa.empresaCodigo).trim()}
              {String(editandoEmpresa.empresaNombre ?? '').trim()
                ? ` · ${String(editandoEmpresa.empresaNombre).trim()}` : ''}
              {String(editandoEmpresa.empresaRepreLegal ?? '').trim()
                ? ` · ${String(editandoEmpresa.empresaRepreLegal).trim()}` : ''}
            </span>
            <CButton size="sm" color="outline-secondary" onClick={() => setEditandoEmpresa(null)}>Cerrar</CButton>
          </CCardHeader>
          <CCardBody>
            {modalError && <div className="alert alert-danger">{modalError}</div>}
            <form onSubmit={guardarEmpresa}>
              {/* [RGM] 2026-09-17 (pedido Rafael): el form se organiza en
                  TARJETAS por sección (antes era una sola parrilla plana). */}
              <CCard className="mb-3">
                <CCardHeader className="py-2 fw-semibold">Datos generales</CCardHeader>
                <CCardBody>
              <div className="row g-3 mb-3">
                <div className="col-md-3">
                  <CFormLabel>Código</CFormLabel>
                  <CFormInput value={editandoEmpresa.empresaCodigo} disabled />
                </div>
                <div className="col-md-3">
                  <CFormLabel>Nombre</CFormLabel>
                  <CFormInput value={formEmpresa.empresaNombre ?? ''}
                    onChange={(e) => setFormEmpresa({ ...formEmpresa, empresaNombre: capitalizarPalabras(e.target.value) })} />
                </div>
                <div className="col-md-3">
                  <CFormLabel>Repre Legal</CFormLabel>
                  <CFormInput value={formEmpresa.empresaRepreLegal ?? ''}
                    onChange={(e) => setFormEmpresa({ ...formEmpresa, empresaRepreLegal: capitalizarPalabras(e.target.value) })} />
                </div>
                <div className="col-md-3">
                  <CFormLabel>Estado</CFormLabel>
                  {/* [RGM] 2026-09-17 (pedido Rafael): SELECT con los estados
                      GX (cuenta.cs:2944-2951) — antes era un número a mano. */}
                  <CFormSelect value={Number(formEmpresa.empresaEstado ?? 0)}
                    onChange={(e) => setFormEmpresa({ ...formEmpresa, empresaEstado: e.target.value })}>
                    <option value={0}>Pendiente</option>
                    <option value={1}>Inactiva</option>
                    <option value={2}>Activa</option>
                    <option value={3}>Mora</option>
                    <option value={4}>Eliminar</option>
                    <option value={5}>Limpia</option>
                    <option value={6}>Prueba</option>
                    <option value={7}>Vacaciones</option>
                    <option value={8}>Cerrada</option>
                  </CFormSelect>
                </div>
                <div className="col-md-3">
                  <CFormLabel>Dirección</CFormLabel>
                  <CFormInput value={formEmpresa.empresaDireccion ?? ''}
                    onChange={(e) => setFormEmpresa({ ...formEmpresa, empresaDireccion: capitalizarPalabras(e.target.value) })} />
                </div>
                <div className="col-md-3">
                  <CFormLabel>Teléfono</CFormLabel>
                  <CFormInput value={formEmpresa.empresaTelefono ?? ''}
                    onChange={(e) => setFormEmpresa({ ...formEmpresa, empresaTelefono: e.target.value })} />
                </div>
                <div className="col-md-3">
                  <CFormLabel>Celular</CFormLabel>
                  <CFormInput value={formEmpresa.empresaCelular ?? ''}
                    onChange={(e) => setFormEmpresa({ ...formEmpresa, empresaCelular: e.target.value })} />
                </div>
                <div className="col-md-3">
                  <CFormLabel>Email</CFormLabel>
                  <CFormInput value={formEmpresa.empresaEmail ?? ''}
                    onChange={(e) => setFormEmpresa({ ...formEmpresa, empresaEmail: e.target.value })} />
                </div>
                <div className="col-md-3">
                  <CFormLabel>País</CFormLabel>
                  <CFormInput value={formEmpresa.empresaPais ?? ''}
                    onChange={(e) => setFormEmpresa({ ...formEmpresa, empresaPais: capitalizarPalabras(e.target.value) })} />
                </div>
                <div className="col-md-3">
                  <CFormLabel>Ciudad</CFormLabel>
                  <CFormInput value={formEmpresa.empresaCiudad ?? ''}
                    onChange={(e) => setFormEmpresa({ ...formEmpresa, empresaCiudad: capitalizarPalabras(e.target.value) })} />
                </div>
                <div className="col-md-3">
                  <CFormLabel>Tipo Moneda</CFormLabel>
                  <CFormInput value={formEmpresa.empresaTipoMoneda ?? ''}
                    onChange={(e) => setFormEmpresa({ ...formEmpresa, empresaTipoMoneda: e.target.value })} />
                </div>
                <div className="col-md-3">
                  <CFormLabel>Motivo Saldo</CFormLabel>
                  <CFormInput value={formEmpresa.empresaMotivoSaldo ?? ''}
                    onChange={(e) => setFormEmpresa({ ...formEmpresa, empresaMotivoSaldo: capitalizarPalabras(e.target.value) })} />
                </div>
                {/* [RGM] 2026-10-02 (moneda global, pedido Rafael): prefijo de
                    la moneda + miles en vivo en Saldo Actual y Total Pagos. */}
                <div className="col-md-3">
                  <CFormLabel>Saldo Actual</CFormLabel>
                  <CInputGroup>
                    <CInputGroupText>{simboloMoneda()}</CInputGroupText>
                    <NumericFormat
                      className="form-control"
                      value={formEmpresa.empresaSaldoActual ?? 0}
                      onValueChange={(v) => setFormEmpresa({ ...formEmpresa, empresaSaldoActual: v.value })}
                      thousandSeparator="," decimalSeparator="." decimalScale={2} fixedDecimalScale
                      allowNegative={false} inputMode="decimal"
                    />
                  </CInputGroup>
                </div>
                <div className="col-md-3">
                  <CFormLabel>Total Pagos</CFormLabel>
                  <CInputGroup>
                    <CInputGroupText>{simboloMoneda()}</CInputGroupText>
                    <NumericFormat
                      className="form-control"
                      value={formEmpresa.empresaTotalPagos ?? 0}
                      onValueChange={(v) => setFormEmpresa({ ...formEmpresa, empresaTotalPagos: v.value })}
                      thousandSeparator="," decimalSeparator="." decimalScale={2} fixedDecimalScale
                      allowNegative={false} inputMode="decimal"
                    />
                  </CInputGroup>
                </div>
              </div>
                </CCardBody>
              </CCard>

              <CCard className="mb-3">
                <CCardHeader className="py-2 fw-semibold">Plan y módulos</CCardHeader>
                <CCardBody>
              <div className="row g-3 mb-3">
                <div className="col-md-3">
                  <CFormLabel>Plan</CFormLabel>
                  {/* [RGM] 2026-09-16 (pedido Rafael): el plan va con SELECT
                      (EmpresaLibreNumerico2 — PlanSiprinia 1/2/3). */}
                  <CFormSelect value={formEmpresa.empresaLibreNumerico2 ?? 0}
                    onChange={(e) => setFormEmpresa({ ...formEmpresa, empresaLibreNumerico2: e.target.value })}>
                    <option value={0}>Sin plan</option>
                    <option value={1}>Unipersonal</option>
                    <option value={2}>Duo</option>
                    <option value={3}>Empresa</option>
                  </CFormSelect>
                </div>
                {/* [RGM] 2026-09-17 (reporte Rafael: "no me sale plan tronia
                    ni el select, en editar empresa"): select del plan TronIA
                    también en el form de edición (EmpresaLibreNumerico3);
                    el estado (PRUEBA/ACTIVO) se cambia en el tab TronIA. */}
                <div className="col-md-3">
                  <CFormLabel>Plan TronIA</CFormLabel>
                  <CFormSelect value={Number(formEmpresa.empresaLibreNumerico3 ?? 0)}
                    onChange={(e) => setFormEmpresa({ ...formEmpresa, empresaLibreNumerico3: e.target.value })}>
                    <option value={0}>Ninguno</option>
                    <option value={1}>Inicial</option>
                    <option value={2}>Profesional</option>
                    <option value={3}>Empresarial</option>
                  </CFormSelect>
                </div>
                {/* [RGM] 2026-09-17 (pedido Rafael): se retiran del form los
                    "Libre Num./Caract./Valor" internos del GX (duplicados de
                    los módulos, sin uso en la UI). */}
                <div className="col-md-3">
                  <CFormLabel>Adicional Mes (Fotos)</CFormLabel>
                  <CFormSwitch label="Activo" id="form-factura"
                    checked={!!formEmpresa.empresaFactura}
                    onChange={(e) => setFormEmpresa({ ...formEmpresa, empresaFactura: e.target.checked })} />
                </div>
                <div className="col-md-3">
                  <CFormLabel>Módulo Facturación</CFormLabel>
                  {/* [RGM] 2026-09-16 (pedido Rafael): módulos con SWITCH
                      (activo si > 0 — smallint GX). */}
                  <CFormSwitch label="Activo" id="form-mod-facturacion"
                    checked={Number(formEmpresa.empresaModuloFacturacion ?? 0) > 0}
                    onChange={(e) => setFormEmpresa({ ...formEmpresa, empresaModuloFacturacion: e.target.checked ? 1 : 0 })} />
                </div>
                {/* Cambio Sencilla y Módulo Domicilio se retiraron del form
                    (módulos que no van — decisión Rafael). El PUT del backend
                    solo pisa los campos que llegan, así que los valores
                    existentes en BD quedan intactos. */}
              </div>
                </CCardBody>
              </CCard>

              <CCard className="mb-3">
                <CCardHeader className="py-2 fw-semibold">Operación</CCardHeader>
                <CCardBody>
              <div className="row g-3 mb-3">
                <div className="col-md-3">
                  <CFormLabel>Descripción</CFormLabel>
                  <CFormInput value={formEmpresa.empresaDescripcion ?? ''}
                    onChange={(e) => setFormEmpresa({ ...formEmpresa, empresaDescripcion: capitalizarPalabras(e.target.value) })} />
                </div>
                <div className="col-md-3">
                  <CFormLabel>Tipo Servicio</CFormLabel>
                  <CFormInput value={formEmpresa.empresaTipoServicio ?? ''}
                    onChange={(e) => setFormEmpresa({ ...formEmpresa, empresaTipoServicio: capitalizarPalabras(e.target.value) })} />
                </div>
                <div className="col-md-3">
                  <CFormLabel>Horario Apertura</CFormLabel>
                  <CFormInput value={formEmpresa.empresaHorarioApertura ?? ''}
                    onChange={(e) => setFormEmpresa({ ...formEmpresa, empresaHorarioApertura: e.target.value })} />
                </div>
                <div className="col-md-3">
                  <CFormLabel>Horario Cierre</CFormLabel>
                  <CFormInput value={formEmpresa.empresaHorarioCierre ?? ''}
                    onChange={(e) => setFormEmpresa({ ...formEmpresa, empresaHorarioCierre: e.target.value })} />
                </div>
                <div className="col-md-3">
                  <CFormLabel>Valor Empaque</CFormLabel>
                  <CInputGroup>
                    <CInputGroupText>{simboloMoneda()}</CInputGroupText>
                    <NumericFormat
                      className="form-control"
                      value={formEmpresa.empresaValorEmpaque ?? 0}
                      onValueChange={(v) => setFormEmpresa({ ...formEmpresa, empresaValorEmpaque: v.value })}
                      thousandSeparator="," decimalSeparator="." decimalScale={2} fixedDecimalScale
                      allowNegative={false} inputMode="decimal"
                    />
                  </CInputGroup>
                </div>
                <div className="col-md-3">
                  <CFormLabel>Actividad Comercial</CFormLabel>
                  <CFormInput type="number" value={formEmpresa.empresaActividadComercial ?? 0}
                    onChange={(e) => setFormEmpresa({ ...formEmpresa, empresaActividadComercial: e.target.value })} />
                </div>
                <div className="col-md-3">
                  <CFormLabel>Último Pago</CFormLabel>
                  <CFormInput type="date" value={String(formEmpresa.empresaUltimoPago ?? '').slice(0, 10)}
                    onChange={(e) => setFormEmpresa({ ...formEmpresa, empresaUltimoPago: e.target.value })} />
                </div>
                <div className="col-md-3">
                  <CFormLabel>Próximo Cobro</CFormLabel>
                  <CFormInput type="date" value={String(formEmpresa.empresaProximoCobro ?? '').slice(0, 10)}
                    onChange={(e) => setFormEmpresa({ ...formEmpresa, empresaProximoCobro: e.target.value })} />
                </div>
                <div className="col-md-3">
                  <CFormLabel>Trabaja Domingo</CFormLabel>
                  <CFormSwitch label="Activo" id="form-domingo"
                    checked={!!formEmpresa.empresaTrabajaDomingo}
                    onChange={(e) => setFormEmpresa({ ...formEmpresa, empresaTrabajaDomingo: e.target.checked })} />
                </div>
                <div className="col-md-3">
                  <CFormLabel>Trabaja Festivo</CFormLabel>
                  <CFormSwitch label="Activo" id="form-festivo"
                    checked={!!formEmpresa.empresaTrabajaFestivo}
                    onChange={(e) => setFormEmpresa({ ...formEmpresa, empresaTrabajaFestivo: e.target.checked })} />
                </div>
              </div>
                </CCardBody>
              </CCard>

              <div className="d-flex justify-content-end gap-2">
                <CButton color="secondary" type="button" disabled={guardandoEmpresa} onClick={() => setEditandoEmpresa(null)}>
                  Cancelar
                </CButton>
                <CButton color="primary" type="submit" disabled={guardandoEmpresa}>
                  {guardandoEmpresa ? <CSpinner size="sm" /> : 'Guardar'}
                </CButton>
              </div>
            </form>
          </CCardBody>
        </CCard>
      )}

      {/* Modal cambiar clave (PUT /superadmin/usuarios/{codigo}/clave) */}
      <CModal visible={claveUsuario !== null} onClose={() => setClaveUsuario(null)}>
        <CModalHeader>
          <CModalTitle>Cambiar clave — {claveUsuario?.usuarioCodigo ?? ''}</CModalTitle>
        </CModalHeader>
        <CModalBody style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          {modalError && <div className="alert alert-danger">{modalError}</div>}
          <form ref={formRef} onSubmit={guardarClave}>
            <CFormLabel>Nueva clave (mínimo 6 caracteres)</CFormLabel>
            <CFormInput type="password" className="mb-3" value={nuevaClave}
              onChange={(e) => setNuevaClave(e.target.value)} />
            <CFormLabel>Confirmación</CFormLabel>
            <CFormInput type="password" value={confirmacion}
              onChange={(e) => setConfirmacion(e.target.value)} />
          </form>
        </CModalBody>
        <CModalFooter className="border-top">
          <CButton color="secondary" type="button" disabled={guardandoClave} onClick={() => setClaveUsuario(null)}>
            Cancelar
          </CButton>
          <CButton color="primary" type="button" disabled={guardandoClave} onClick={() => formRef.current?.requestSubmit()}>
            {guardandoClave ? <CSpinner size="sm" /> : 'Guardar'}
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default SuperAdminEmpresasPage
